import { ArrowRightLeft, Link } from "lucide-react";
import {
  OwnerIndicator,
  normalizeOwners,
  HIDDEN_HANDLE_STYLE,
} from "../OwnerIndicator";
import { Node, Handle, Position, useNodeConnections } from "@xyflow/react";
import { EventCatalogResource, Channel as ChannelType } from "../../types";
import { NotesIndicator } from "../NotesIndicator";
import {
  LINE_CLAMP_STYLE,
  FOLDED_CORNER_SHADOW_STYLE,
  EMPTY_ARRAY,
  useDarkMode,
} from "../shared-styles";
import { CustomIcon, isIconPath } from "../../utils/custom-icon";
import { TruncatedResourceName } from "../TruncatedResourceName";
import { FocusedResourceIndicator } from "../FocusedResourceIndicator";

import { memo, useMemo } from "react";

const DELIVERY_GUARANTEE_LABELS: Record<string, string> = {
  "at-most-once": "At most once",
  "at-least-once": "At least once",
  "exactly-once": "Exactly once",
};

const GUARANTEE_COLORS: Record<string, { hex: string; rgb: string }> = {
  "exactly-once": { hex: "#22d3ee", rgb: "34,211,238" },
  "at-least-once": { hex: "#a78bfa", rgb: "167,139,250" },
  "at-most-once": { hex: "#f87171", rgb: "248,113,113" },
};

const DEFAULT_GUARANTEE = { hex: "#6b7280", rgb: "107,114,128" };

function getGuarantee(guarantee?: string) {
  return (guarantee && GUARANTEE_COLORS[guarantee]) || DEFAULT_GUARANTEE;
}

/**
 * Delivery guarantee dot icon.
 * - exactly-once  → filled circle
 * - at-least-once → half-filled circle
 * - at-most-once  → hollow ring
 */
function GuaranteeDot({
  guarantee,
  size = 6,
}: {
  guarantee: string;
  size?: number;
}) {
  const { hex } = getGuarantee(guarantee);

  if (guarantee === "exactly-once") {
    return (
      <span
        className="shrink-0 rounded-full"
        style={{ width: size, height: size, background: hex }}
      />
    );
  }

  if (guarantee === "at-least-once") {
    return (
      <span
        className="shrink-0 rounded-full overflow-hidden"
        style={{
          width: size,
          height: size,
          background: `linear-gradient(90deg, ${hex} 50%, transparent 50%)`,
          border: `1.5px solid ${hex}`,
          boxSizing: "border-box",
        }}
      />
    );
  }

  // at-most-once → hollow ring
  return (
    <span
      className="shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        border: `1.5px solid ${hex}`,
        boxSizing: "border-box",
      }}
    />
  );
}

function GlowHandle({ side }: { side: "left" | "right" }) {
  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        [side]: -6,
        transform: "translateY(-50%)",
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #6b7280, #374151)",
        border: "2px solid rgb(var(--ec-page-bg))",
        zIndex: 20,
        animation: "ec-channel-handle-pulse 2s ease-in-out infinite",
        pointerEvents: "none",
      }}
    />
  );
}

type ChannelNodeData = EventCatalogResource & {
  channel: ChannelType;
};

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export type ChannelNode = Node<ChannelNodeData, "channel">;

function PostItChannel(props: ChannelNode) {
  const { data } = props;
  const {
    version,
    name,
    summary,
    deprecated,
    draft,
    protocols = EMPTY_ARRAY,
    notes,
    deliveryGuarantee,
    styles,
  } = data.channel;
  const mode = props.data.mode || "simple";
  const guarantee = getGuarantee(deliveryGuarantee);
  const customIcon = isIconPath(styles?.icon) ? styles!.icon! : undefined;

  return (
    <div
      className={classNames(
        "relative min-w-44 max-w-56 min-h-[120px]",
        props?.selected ? "ring-2 ring-gray-400/60 ring-offset-1" : "",
      )}
    >
      {props.data.isFocused && <FocusedResourceIndicator />}
      <Handle
        type="target"
        position={Position.Left}
        style={HIDDEN_HANDLE_STYLE}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={HIDDEN_HANDLE_STYLE}
      />
      {notes && notes.length > 0 && (
        <NotesIndicator notes={notes} resourceName={name} />
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #e5e7eb 0%, #d1d5db 40%, #6b7280 100%)",
          boxShadow:
            "1px 1px 3px rgba(0,0,0,0.15), 3px 4px 8px rgba(0,0,0,0.08)",
          transform: "rotate(-1deg)",
          border: deprecated
            ? "2px dashed rgba(239, 68, 68, 0.5)"
            : draft
              ? "2px dashed rgba(107, 114, 128, 0.5)"
              : "none",
        }}
      >
        {/* Folded corner */}
        <div style={FOLDED_CORNER_SHADOW_STYLE} />
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 0,
            height: 0,
            borderStyle: "solid",
            borderWidth: "18px 0 0 18px",
            borderColor: "#374151 transparent transparent transparent",
            opacity: 0.3,
          }}
        />
      </div>

      <div className="relative px-3.5 py-3">
        {/* Type badge row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <ArrowRightLeft
              className="w-3 h-3 text-gray-900/50"
              strokeWidth={2.5}
            />
            <span className="text-[8px] font-bold text-gray-900/50 uppercase tracking-widest">
              Channel
            </span>
          </div>
          <div className="flex items-center gap-1">
            {draft && (
              <span className="text-[8px] font-extrabold text-amber-900 bg-amber-100 border border-dashed border-amber-400 px-1.5 py-0.5 rounded uppercase">
                Draft
              </span>
            )}
            {deprecated && (
              <span className="text-[7px] font-bold text-white bg-red-500 border border-red-600 px-1.5 py-0.5 rounded uppercase">
                Deprecated
              </span>
            )}
          </div>
        </div>

        {/* Name + version */}
        <div className="flex items-center gap-2">
          {customIcon && (
            <CustomIcon
              src={customIcon}
              alt={name}
              className={
                mode === "full"
                  ? "w-5 h-5 shrink-0 mt-0.5"
                  : "w-4 h-4 shrink-0 -my-1"
              }
            />
          )}
          <div className="flex items-baseline gap-1.5 min-w-0">
            <TruncatedResourceName
              as="div"
              value={name}
              tooltipBorderColor="#6b7280"
              className={classNames(
                "text-[13px] font-bold leading-snug truncate",
                deprecated ? "text-gray-950/40 line-through" : "text-gray-950",
              )}
            >
              {name}
            </TruncatedResourceName>
            {version && (
              <span className="text-[9px] text-gray-900/30 font-medium shrink-0">
                v{version}
              </span>
            )}
          </div>
        </div>

        {/* Summary */}
        {mode === "full" && summary && (
          <div
            className="mt-1 text-[9px] text-gray-950/50 leading-relaxed overflow-hidden"
            style={LINE_CLAMP_STYLE}
            title={summary}
          >
            {summary}
          </div>
        )}

        {/* Operational row: delivery guarantee + protocol pills */}
        {(deliveryGuarantee || protocols?.[0]) && (
          <div className="flex items-center gap-1 flex-wrap mt-2">
            {deliveryGuarantee && (
              <span
                className="inline-flex items-center gap-1 text-[7px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5"
                style={{
                  background: `rgba(${guarantee.rgb}, 0.12)`,
                  color: guarantee.hex,
                }}
              >
                <GuaranteeDot guarantee={deliveryGuarantee} size={5} />
                {DELIVERY_GUARANTEE_LABELS[deliveryGuarantee] ||
                  deliveryGuarantee}
              </span>
            )}
            {protocols?.[0] && (
              <span className="inline-flex items-center text-[7px] font-semibold text-gray-900/50 bg-gray-900/10 rounded-full px-1.5 py-0.5 uppercase tracking-wide">
                {protocols[0]}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DefaultChannel(props: ChannelNode) {
  const { data } = props;
  const {
    version,
    name,
    summary,
    deprecated,
    draft,
    protocols = EMPTY_ARRAY,
    address,
    notes,
    deliveryGuarantee,
    styles,
  } = data.channel;
  const mode = props.data.mode || "simple";
  const customIcon = isIconPath(styles?.icon) ? styles!.icon! : undefined;
  const owners = useMemo(
    () => normalizeOwners(data.channel?.owners),
    [data.channel?.owners],
  );
  const sourceConnections = useNodeConnections({ handleType: "source" });
  const targetConnections = useNodeConnections({ handleType: "target" });
  const isDark = useDarkMode();
  const deprecatedStripe = isDark
    ? "rgba(239,68,68,0.25)"
    : "rgba(239,68,68,0.1)";
  const guarantee = getGuarantee(deliveryGuarantee);

  return (
    <div
      className={classNames(
        "relative min-w-48 max-w-60 rounded-xl border-2 overflow-visible",
        props?.selected ? "ring-2 ring-gray-400/60 ring-offset-2" : "",
        deprecated
          ? "border-dashed border-red-500"
          : draft
            ? `border-dashed ${isDark ? "border-gray-400" : "border-gray-400/60"}`
            : isDark
              ? "border-gray-400"
              : "border-gray-500",
      )}
      style={{
        background: deprecated
          ? `repeating-linear-gradient(135deg, transparent, transparent 6px, ${deprecatedStripe} 6px, ${deprecatedStripe} 7px), var(--ec-channel-node-bg, rgb(var(--ec-card-bg)))`
          : draft
            ? `repeating-linear-gradient(135deg, transparent, transparent 4px, ${isDark ? "rgba(107,114,128,0.25)" : "rgba(107,114,128,0.15)"} 4px, ${isDark ? "rgba(107,114,128,0.25)" : "rgba(107,114,128,0.15)"} 4.5px), repeating-linear-gradient(45deg, transparent, transparent 4px, ${isDark ? "rgba(107,114,128,0.25)" : "rgba(107,114,128,0.15)"} 4px, ${isDark ? "rgba(107,114,128,0.25)" : "rgba(107,114,128,0.15)"} 4.5px), var(--ec-channel-node-bg, rgb(var(--ec-card-bg)))`
            : "var(--ec-channel-node-bg, rgb(var(--ec-card-bg)))",
        boxShadow: "0 2px 12px rgba(107, 114, 128, 0.15)",
      }}
    >
      {props.data.isFocused && <FocusedResourceIndicator />}
      <Handle
        type="target"
        position={Position.Left}
        style={HIDDEN_HANDLE_STYLE}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={HIDDEN_HANDLE_STYLE}
      />
      {notes && notes.length > 0 && (
        <NotesIndicator notes={notes} resourceName={name} />
      )}
      {targetConnections.length > 0 && <GlowHandle side="left" />}
      {sourceConnections.length > 0 && <GlowHandle side="right" />}

      {/* Type badge top-left */}
      <div className="absolute -top-2.5 left-2.5 z-10">
        <span
          className={classNames(
            "inline-flex items-center gap-1 text-[7px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5 rounded shadow-sm",
            deprecated ? "bg-red-500" : "bg-gray-500",
          )}
        >
          <ArrowRightLeft className="w-2.5 h-2.5" strokeWidth={2.5} />
          Channel{draft && " (Draft)"}
          {deprecated && " (Deprecated)"}
        </span>
      </div>
      {/* Protocol badge top-right — removed, now in operational row */}

      <div className="px-3 pt-3.5 pb-2.5">
        {/* Name + version */}
        <div className="flex items-center gap-2">
          {customIcon && (
            <CustomIcon
              src={customIcon}
              alt={name}
              className={
                mode === "full"
                  ? "w-5 h-5 shrink-0 mt-0.5"
                  : "w-4 h-4 shrink-0 -my-1"
              }
            />
          )}
          <div className="flex items-baseline gap-1.5 min-w-0">
            <TruncatedResourceName
              value={name}
              tooltipBorderColor="#6b7280"
              className="text-[13px] font-semibold leading-snug text-[rgb(var(--ec-page-text))] truncate"
            >
              {name}
            </TruncatedResourceName>
            {version && (
              <span
                className="text-[9px] font-normal shrink-0"
                style={{
                  color: "rgb(var(--ec-page-text-muted))",
                  opacity: 0.5,
                }}
              >
                v{version}
              </span>
            )}
          </div>
        </div>

        {/* Summary */}
        {mode === "full" && summary && (
          <div
            className="mt-1 text-[9px] text-[rgb(var(--ec-page-text-muted))] leading-relaxed overflow-hidden"
            style={{ ...LINE_CLAMP_STYLE, marginBottom: 4 }}
            title={summary}
          >
            {summary}
          </div>
        )}

        {/* Address */}
        {mode === "full" && address && (
          <div className="flex items-center gap-1 mt-1">
            <Link className="w-2.5 h-2.5 text-[rgb(var(--ec-page-text-muted))]" />
            <span className="text-[8px] text-[rgb(var(--ec-page-text-muted))] font-mono">
              {address}
            </span>
          </div>
        )}

        {/* Operational row: delivery guarantee + protocol pills */}
        {(deliveryGuarantee || protocols?.[0]) && (
          <div className="flex items-center gap-1 flex-wrap mt-2">
            {deliveryGuarantee && (
              <span
                className="inline-flex items-center gap-1 text-[7px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5"
                style={{
                  background: `rgba(${guarantee.rgb}, 0.08)`,
                  color: guarantee.hex,
                }}
              >
                <GuaranteeDot guarantee={deliveryGuarantee} size={5} />
                {DELIVERY_GUARANTEE_LABELS[deliveryGuarantee] ||
                  deliveryGuarantee}
              </span>
            )}
            {protocols?.[0] && (
              <span
                className="inline-flex items-center text-[7px] font-semibold rounded-full px-1.5 py-0.5 uppercase tracking-wide"
                style={{
                  background: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(107,114,128,0.1)",
                  color: "rgb(var(--ec-page-text-muted))",
                }}
              >
                {protocols[0]}
              </span>
            )}
          </div>
        )}

        {/* Owners */}
        <OwnerIndicator
          owners={owners}
          accentColor="bg-gray-400"
          borderColor="rgba(107,114,128,0.08)"
          iconClass="text-gray-300"
        />
      </div>
    </div>
  );
}

export default memo(function Channel(props: ChannelNode) {
  const nodeStyle = props?.data?.style;

  if (nodeStyle === "post-it") {
    return <PostItChannel {...props} />;
  }

  return <DefaultChannel {...props} />;
});
